import { useReducer } from 'react';

import { AssetContentTypeEnum } from 'src/graphql/schema';
import { type IAction } from 'src/utils/ts-utilities';

enum ActionEnum {
  ADD_DELETED_ASSET = 'add_deleted_asset',
  UNDO_ASSET_DELETION = 'undo_asset_deletion',
  SET_INITIAL_DATA = 'set_initial_data',
}

type IDeletedAsset = {
  id: string;
  name: string;
};

type AssetType = 'unlabeled' | 'credentials' | AssetContentTypeEnum;

type IActionReducer =
  | IAction<
      ActionEnum.ADD_DELETED_ASSET,
      {
        assetType: AssetType;
        asset: IDeletedAsset;
      }
    >
  | IAction<ActionEnum.UNDO_ASSET_DELETION, { assetType: AssetType; assetId: string }>
  | IAction<ActionEnum.SET_INITIAL_DATA>;

type IState = {
  unlabeled: IDeletedAsset[];
  credentials: IDeletedAsset[];
  [AssetContentTypeEnum.Data]: IDeletedAsset[];
  [AssetContentTypeEnum.Documentation]: IDeletedAsset[];
  [AssetContentTypeEnum.SourceCode]: IDeletedAsset[];
  [AssetContentTypeEnum.Deployment]: IDeletedAsset[];
  [AssetContentTypeEnum.ThirdPartyDependency]: IDeletedAsset[];
};

const initialState: IState = {
  unlabeled: [],
  credentials: [],
  [AssetContentTypeEnum.Data]: [],
  [AssetContentTypeEnum.Documentation]: [],
  [AssetContentTypeEnum.SourceCode]: [],
  [AssetContentTypeEnum.Deployment]: [],
  [AssetContentTypeEnum.ThirdPartyDependency]: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.ADD_DELETED_ASSET: {
      const { assetType, asset } = action.payload;
      // Prevent duplicates
      const isDuplicate = state[assetType as keyof typeof state].some((a) => a.id === asset.id);
      if (isDuplicate) return state;

      return {
        ...state,
        [assetType]: [...state[assetType as keyof typeof state], asset],
      };
    }
    case ActionEnum.UNDO_ASSET_DELETION: {
      const { assetType, assetId } = action.payload;
      const filteredAssets = state[assetType as keyof typeof state].filter((asset) => asset.id !== assetId);

      return {
        ...state,
        [assetType]: filteredAssets,
      };
    }
    case ActionEnum.SET_INITIAL_DATA:
      return initialState;
    default:
      return state;
  }
};

const getAllDeletedAssets = (state: IState): IDeletedAsset[] => {
  return [
    ...state.unlabeled,
    ...state[AssetContentTypeEnum.Data],
    ...state[AssetContentTypeEnum.Documentation],
    ...state[AssetContentTypeEnum.SourceCode],
    ...state[AssetContentTypeEnum.Deployment],
    ...state[AssetContentTypeEnum.ThirdPartyDependency],
  ];
};

export const useDepositsSuggestChangesSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addDeletedAsset = (assetType: AssetType, asset: IDeletedAsset) => {
    dispatch({
      type: ActionEnum.ADD_DELETED_ASSET,
      payload: { assetType, asset },
    });
  };

  const undoAssetDeletion = (assetType: AssetType, assetId: string) => {
    dispatch({
      type: ActionEnum.UNDO_ASSET_DELETION,
      payload: { assetType, assetId },
    });
  };

  const setInitialData = () =>
    dispatch({
      type: ActionEnum.SET_INITIAL_DATA,
      payload: {},
    });

  const allDeletedAssets = getAllDeletedAssets(state);

  return {
    state,
    allDeletedAssets,
    allDeletedCredentials: state.credentials,
    addDeletedAsset,
    undoAssetDeletion,
    setInitialData,
  };
};
